import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Download, Loader2, ShieldCheck, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminModal({ open, onClose }) {
  const [password, setPassword] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [news, setNews] = useState([]);
  const [tab, setTab] = useState("enquiries");
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [post, setPost] = useState({ title: "", date: "", excerpt: "" });
  const [imageFile, setImageFile] = useState(null);

  const headers = { "X-Admin-Key": password };

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const [en, nw] = await Promise.all([
        axios.get(`${API}/enquiries`, { headers }),
        axios.get(`${API}/news`),
      ]);
      setEnquiries(en.data);
      setNews(nw.data);
    } catch {
      toast.error("Wrong admin password.");
    } finally {
      setLoading(false);
    }
  };

  const refreshNews = async () => {
    const res = await axios.get(`${API}/news`);
    setNews(res.data);
  };

  const publish = async (e) => {
    e.preventDefault();
    if (!post.title.trim() || !post.date.trim()) {
      toast.error("Title and date are required.");
      return;
    }
    setPosting(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await axios.post(`${API}/upload?kind=news`, fd, { headers });
        imageUrl = up.data.url;
      }
      await axios.post(`${API}/news`, {
        title: post.title.trim(),
        date: post.date.trim(),
        excerpt: post.excerpt.trim() || null,
        image: imageUrl,
      }, { headers });
      toast.success("News published.");
      setPost({ title: "", date: "", excerpt: "" });
      setImageFile(null);
      await refreshNews();
    } catch {
      toast.error("Could not publish. Try again.");
    } finally {
      setPosting(false);
    }
  };

  const removePost = async (id) => {
    try {
      await axios.delete(`${API}/news/${id}`, { headers });
      toast.success("Post deleted.");
      setNews((n) => n.filter((p) => p.id !== id));
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Date", "Type", "Name", "Phone", "Email", "Company", "Product", "Message"],
      ...enquiries.map((q) => [q.created_at, q.enquiry_type, q.name, q.phone, q.email || "", q.company || "", q.product || "", (q.message || "").replace(/\n/g, " ")]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "laneeka-enquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const close = () => {
    onClose();
    setEnquiries(null);
    setPassword("");
    setTab("enquiries");
  };

  const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:border-orange-600 focus:outline-none";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="admin-enquiries-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white w-full max-w-3xl rounded-xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-8 py-5 border-b border-slate-200">
              <p className="font-bold text-xl flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-orange-600" /> Admin Panel
              </p>
              <button data-testid="admin-modal-close" onClick={close} className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-md hover:bg-orange-600 transition-colors" aria-label="Close admin">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!enquiries ? (
              <form onSubmit={load} className="p-8">
                <p className="text-sm text-slate-600 mb-4">Enter the admin password to manage enquiries and news.</p>
                <input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Admin password"
                  className={`${inputCls} mb-4`}
                />
                <button
                  data-testid="admin-login-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white font-semibold py-3.5 rounded-md hover:bg-orange-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Log In
                </button>
              </form>
            ) : (
              <>
                <div className="px-8 pt-4 flex gap-2 border-b border-slate-100">
                  {["enquiries", "news"].map((t) => (
                    <button
                      key={t}
                      data-testid={`admin-tab-${t}`}
                      onClick={() => setTab(t)}
                      className={`px-4 py-2 text-sm font-semibold rounded-t-md capitalize ${tab === t ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                    >
                      {t === "enquiries" ? `Enquiries (${enquiries.length})` : `News (${news.length})`}
                    </button>
                  ))}
                </div>

                {tab === "enquiries" ? (
                  <>
                    <div className="px-8 py-3 flex items-center justify-end border-b border-slate-100">
                      {enquiries.length > 0 && (
                        <button data-testid="admin-export-csv" onClick={exportCsv} className="flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-slate-900 transition-colors">
                          <Download className="w-4 h-4" /> Export CSV
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto p-8 pt-4 space-y-3" data-testid="admin-enquiry-list">
                      {enquiries.length === 0 && <p className="text-center text-slate-500 py-10">No enquiries yet.</p>}
                      {enquiries.map((q) => (
                        <div key={q.id} data-testid={`admin-enquiry-${q.id}`} className="border border-slate-200 rounded-lg p-5 hover:border-orange-600 transition-colors">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <p className="font-bold">{q.name} <span className="font-mono text-xs font-normal text-slate-500 ml-2">{q.phone}</span></p>
                            <span className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded ${q.enquiry_type === "quote" ? "bg-orange-100 text-orange-700" : q.enquiry_type === "career" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                              {q.enquiry_type}
                            </span>
                          </div>
                          {q.product && <p className="text-sm font-semibold text-orange-600 mb-1">{q.product}</p>}
                          {q.message && <p className="text-sm text-slate-600 leading-relaxed">{q.message}</p>}
                          <p className="mt-2 font-mono text-[10px] text-slate-400">
                            {new Date(q.created_at).toLocaleString("en-GB")} {q.company ? `· ${q.company}` : ""} {q.email ? `· ${q.email}` : ""}
                          </p>
                          {q.resume_path && (
                            <a
                              data-testid={`admin-resume-${q.id}`}
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${q.resume_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:underline"
                            >
                              <Download className="w-3.5 h-3.5" /> Download CV
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="overflow-y-auto p-8 pt-4" data-testid="admin-news-panel">
                    <form onSubmit={publish} className="border border-slate-200 rounded-lg p-5 mb-6 bg-slate-50">
                      <p className="font-bold text-sm mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Publish new post</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input data-testid="news-input-title" value={post.title} onChange={(e) => setPost((p) => ({ ...p, title: e.target.value }))} placeholder="Post title *" className={inputCls} />
                        <input data-testid="news-input-date" value={post.date} onChange={(e) => setPost((p) => ({ ...p, date: e.target.value }))} placeholder="Date (e.g. July 15, 2026) *" className={inputCls} />
                      </div>
                      <textarea data-testid="news-input-excerpt" rows={3} value={post.excerpt} onChange={(e) => setPost((p) => ({ ...p, excerpt: e.target.value }))} placeholder="Short description…" className={`${inputCls} mt-3 resize-none`} />
                      <label className="block mt-3">
                        <span className="block text-xs font-medium text-slate-500 mb-1.5">Thumbnail image (optional)</span>
                        <input data-testid="news-input-image" type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0] || null)} className="text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:text-xs file:font-semibold hover:file:bg-orange-600" />
                      </label>
                      <button data-testid="news-publish-button" type="submit" disabled={posting}
                        className="mt-4 w-full bg-slate-900 text-white text-sm font-semibold py-2.5 rounded-md hover:bg-orange-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                        {posting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {posting ? "Publishing…" : "Publish Post"}
                      </button>
                    </form>

                    <div className="space-y-3" data-testid="admin-news-list">
                      {news.map((n) => (
                        <div key={n.id} data-testid={`admin-news-${n.id}`} className="border border-slate-200 rounded-lg p-4 flex items-center gap-4">
                          {n.image && <img src={n.image.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${n.image}` : n.image} alt="" className="w-16 h-16 object-cover rounded-md shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{n.title}</p>
                            <p className="text-xs text-slate-500">{n.date}</p>
                          </div>
                          <button data-testid={`news-delete-${n.id}`} onClick={() => removePost(n.id)}
                            className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-md transition-colors shrink-0" aria-label="Delete post">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
